// src/features/auth/components/SessionTimeout.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useAuth } from '../../../context/AuthContext';
import { useInteraction } from '../../../context/InteractionContext';
import { tokenService } from '../services/tokenService';
import { authService } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';

const SESSION_WARNING_TIME = 10 * 60 * 1000; // 10 minutes before expiration (was 5 minutes)
const CHECK_INTERVAL = 1000; // Check every second
const VISIBILITY_CHECK_INTERVAL = 5000; // Check visibility every 5 seconds when hidden
const AUTO_EXTEND_IDLE_THRESHOLD = 3 * 60 * 1000; // auto-extend if last interaction within 3 minutes

export default function SessionTimeout() {
  const navigate = useNavigate();
  const { logout, isAuthenticated } = useAuth();
  const { getLastInteractionAt } = useInteraction();
  const [showWarning, setShowWarning] = useState(false);
  const [isExtending, setIsExtending] = useState(false);
  const [extendError, setExtendError] = useState(null);
  const [, setForceUpdate] = useState({}); // Force re-renders for countdown
  const { t } = useTranslation(["auth", "common"]);

  // Use refs to track intervals and avoid stale closures
  const sessionCheckRef = useRef();
  const visibilityCheckRef = useRef();
  const countdownRef = useRef();
  const wasHiddenRef = useRef(false);
  const extendSessionRef = useRef(null);

  const switch_user = useCallback(() => {
    navigate("/auth/change-user");
  }, [navigate]);

  // Calculate time left based on actual token expiration (always fresh)
  const getActualTimeLeft = useCallback(() => {
    return tokenService.getTimeUntilExpiration();
  }, []);

  // Check if we should show warning based on actual time
  const shouldShowWarning = useCallback(() => {
    const timeLeft = getActualTimeLeft();
    return timeLeft > 0 && timeLeft <= SESSION_WARNING_TIME;
  }, [getActualTimeLeft]);

  // Handle visibility change to sync when tab becomes visible again
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible' && wasHiddenRef.current) {
      console.log('SessionTimeout: Tab became visible, checking session immediately');
      wasHiddenRef.current = false;
      
      // Immediately check session status when tab becomes visible
      const timeLeft = getActualTimeLeft();
      console.log("SessionTimeout: ", timeLeft/1000, " seconds left")
      
      if (timeLeft <= 0) {
        console.log('SessionTimeout: Token expired while tab was hidden');
        switch_user();
        return;
      }
      
      if (shouldShowWarning() && !showWarning) {
        console.log('SessionTimeout: Should show warning after tab became visible');
        setShowWarning(true);
        setExtendError(null);
      } else if (!shouldShowWarning() && showWarning) {
        console.log('SessionTimeout: Hiding warning after tab became visible');
        setShowWarning(false);
      }
    } else if (document.visibilityState === 'hidden') {
      wasHiddenRef.current = true;
    }
  }, [getActualTimeLeft, shouldShowWarning, showWarning, switch_user]);

  // Main session monitoring function
  const checkSession = useCallback(() => {
    if (!isAuthenticated) return;

    const timeLeft = getActualTimeLeft();
    
    // If token has already expired, logout immediately
    if (timeLeft <= 0) {
      console.log('SessionTimeout: Token expired, logging out');
      switch_user();
      return;
    }

    // Show warning if we're within the warning threshold
    if (shouldShowWarning()) {
      if (!showWarning) {
        const timeSinceInteraction = Date.now() - getLastInteractionAt();
        if (timeSinceInteraction < AUTO_EXTEND_IDLE_THRESHOLD) {
          console.log('SessionTimeout: User recently active, auto-extending silently');
          extendSessionRef.current?.();
          return;
        }
        console.log('SessionTimeout: Showing warning dialog', {
          secondsUntilExpiration: Math.floor(timeLeft / 1000)
        });
        setShowWarning(true);
        setExtendError(null);
      }
    } else {
      if (showWarning) {
        console.log('SessionTimeout: Hiding warning dialog');
        setShowWarning(false);
      }
    }
  }, [isAuthenticated, getActualTimeLeft, shouldShowWarning, showWarning, switch_user, getLastInteractionAt]);

  // Setup session monitoring
  useEffect(() => {
    if (!isAuthenticated) {
      // Clear all states when not authenticated
      setShowWarning(false);
      setIsExtending(false);
      setExtendError(null);

      // Clear intervals
      if (sessionCheckRef.current) {
        clearInterval(sessionCheckRef.current);
        sessionCheckRef.current = null;
      }
      if (visibilityCheckRef.current) {
        clearInterval(visibilityCheckRef.current);
        visibilityCheckRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      
      return;
    }

    // Initial check
    checkSession();

    // Set up regular session checking
    sessionCheckRef.current = setInterval(checkSession, CHECK_INTERVAL);

    // Set up visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Set up interval for when document is hidden (less frequent checks)
    visibilityCheckRef.current = setInterval(() => {
      if (document.visibilityState === 'hidden') {
        checkSession();
      }
    }, VISIBILITY_CHECK_INTERVAL);

    return () => {
      if (sessionCheckRef.current) {
        clearInterval(sessionCheckRef.current);
        sessionCheckRef.current = null;
      }
      if (visibilityCheckRef.current) {
        clearInterval(visibilityCheckRef.current);
        visibilityCheckRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, checkSession, handleVisibilityChange]);

  // Setup countdown timer for display updates when warning is showing
  useEffect(() => {
    if (showWarning && isAuthenticated) {
      // Update display every second when warning is shown
      countdownRef.current = setInterval(() => {
        setForceUpdate({}); // Trigger re-render to update countdown display
        
        // Check the actual time left and display time left
        const actualTimeLeft = getActualTimeLeft();
        const displayTimeLeft = Math.max(0, actualTimeLeft - (5 * 60 * 1000));
        console.log(displayTimeLeft)
        
        // Switch user when display countdown reaches zero (5 minutes before expiration)
        if (displayTimeLeft <= 0) {
          console.log('SessionTimeout: Display countdown reached zero (5 min before expiration), switching user');
          switch_user();
        } else if (actualTimeLeft <= 0) {
          console.log('SessionTimeout: Token expired, switching user');
          switch_user();
        } else if (!shouldShowWarning()) {
          console.log('SessionTimeout: No longer need to show warning');
          setShowWarning(false);
        }
      }, 1000);
    } else if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [showWarning, isAuthenticated, getActualTimeLeft, shouldShowWarning, switch_user]);

  const handleExtendSession = useCallback(async () => {
    if (isExtending) return;

    setIsExtending(true);
    setExtendError(null);

    try {
      console.log('SessionTimeout: Attempting to refresh token');
      
      const refreshResult = await authService.refreshToken();
      
      if (refreshResult) {
        console.log('SessionTimeout: Token refreshed successfully');
        setShowWarning(false);
      } else {
        throw new Error('Token refresh failed');
      }
    } catch (error) {
      console.error('SessionTimeout: Failed to extend session:', error);
      setExtendError('Failed to extend session. You will be logged out.');
      
      // Auto-logout after showing error for a few seconds
      setTimeout(() => {
        switch_user();
      }, 3000);
    } finally {
      setIsExtending(false);
    }
  }, [isExtending, switch_user]);

  extendSessionRef.current = handleExtendSession;

  const handleLogout = useCallback(() => {
    console.log('SessionTimeout: User chose to logout');
    switch_user();
  }, [switch_user]);

  const formatTime = useCallback((milliseconds) => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  // Don't render anything if not authenticated or not showing warning
  if (!isAuthenticated || !showWarning) {
    return null;
  }

  // Always get fresh time left for display
  const actualTimeLeft = getActualTimeLeft();
  // Display time counting down to 5 minutes (300 seconds) before expiration
  const displayTimeLeft = Math.max(0, actualTimeLeft - (5 * 60 * 1000));
  const currentTimeLeft = displayTimeLeft;
  const progressValue = displayTimeLeft > 0 ? 
    ((SESSION_WARNING_TIME - (5 * 60 * 1000) - displayTimeLeft) / (SESSION_WARNING_TIME - (5 * 60 * 1000))) * 100 : 100;

  return (
    <Dialog
      open={showWarning}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'warning.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <WarningIcon sx={{ color: 'white' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {t("auth:session_timeout.timeout_warning")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("auth:session_timeout.expire_warning")}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {extendError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {extendError}
          </Alert>
        ) : (
          <>
            <Typography variant="body1" paragraph>
              <Trans 
                i18nKey="auth:session_timeout.message_with_formatting"
                values={{ timeLeft: formatTime(currentTimeLeft) }}
                components={{ 
                  strong: <strong /> 
                }}
              />
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              {t("auth:session_timeout.prompt")}
            </Typography>
          </>
        )}

        <Box sx={{ mt: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="caption" color="text.secondary">
              {t("auth:session_timeout.time_remaning")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatTime(currentTimeLeft)}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, Math.max(0, progressValue))}
            color="warning"
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                borderRadius: 4,
              }
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button 
          onClick={handleLogout} 
          color="inherit"
          disabled={isExtending}
        >
          {t("auth:session_timeout.logout_now")}
        </Button>
        <Button
          onClick={handleExtendSession}
          variant="contained"
          color="primary"
          disabled={isExtending || !!extendError}
          startIcon={isExtending ? <ScheduleIcon /> : null}
        >
          {isExtending ? t("auth:session_timeout.extending") : t("auth:session_timeout.extend")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}